// Research / Insider trades: look up a stock and see its insider trades from SEC
// Form 4 filings over the range you choose, pinned on its price chart. The newest
// filings arrive first; older ones load a page at a time behind them.

import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";

import { InsiderChart } from "../components/InsiderChart";
import { compactUsd, count, price } from "../format";
import {
  capSize, CHART_RANGES, clusterBuying, EXAMPLES, filterTrades, lookUp, normalizeTicker, SIZES, suggest, summarize, TRADE_TYPES,
  clock, customWindow, DEFAULT_LOOKBACK, EARLIEST, LONG_FILINGS, LOOKBACKS, monthsBefore, monthsBetween, nextPages, presetWindow, reachedPercent,
  secondsLeft, shorterPreset,
  TYPE_LABELS, yearSegments, type PinGroup, type Sample, type Suggestion, type TickerPage, type TradeType, type Window,
} from "../insider";
import styles from "./InsiderTrades.module.css";

const ROWS = 200;

const shares = (n: number | null) => (n == null ? "—" : count(Math.round(n)));
const capLabel = (cap: number) => (cap >= 1e12 ? `$${(cap / 1e12).toFixed(2)}T` : compactUsd(cap));
const monthYear = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const fromDay = (day: string) => monthYear.format(new Date(`${day}T00:00:00Z`));
const today = () => new Date().toISOString().slice(0, 10);

/** Resolves after `ms`, or at once when `signal` aborts. */
const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

/** A preset from LOOKBACKS, or a custom window. */
type Range = { preset: string } | Window;

const windowOf = (range: Range): Window => ("preset" in range ? presetWindow(range.preset, today()) : range);

function initialState(): { ticker: string | null; range: Range } {
  const params = new URLSearchParams(location.search);
  const preset = params.get("range");
  const custom = customWindow(params.get("from") ?? "", params.get("to") ?? "", today());
  const range = preset && LOOKBACKS.some(([id]) => id === preset) ? { preset } : custom ?? { preset: DEFAULT_LOOKBACK };
  return { ticker: normalizeTicker(params.get("ticker") ?? ""), range };
}

/** The ticker and range in the address bar, so a link opens the same view. */
function writeUrl(ticker: string | null, range: Range) {
  const params = new URLSearchParams();
  if (ticker) params.set("ticker", ticker);
  if (!("preset" in range)) {
    params.set("from", range.since);
    params.set("to", range.until);
  } else if (range.preset !== DEFAULT_LOOKBACK) params.set("range", range.preset);
  const query = params.toString();
  history.replaceState(null, "", query ? `${location.pathname}?${query}` : location.pathname);
}

/** "since Oct 2021" when the range runs to today, otherwise "from Jan 2020 to Dec 2023". */
const describe = (window: Window) => (window.until === today() ? `since ${fromDay(window.since)}` : `from ${fromDay(window.since)} to ${fromDay(window.until)}`);

function toggle<T>(set: ReadonlySet<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

/** An iOS-style activity indicator: twelve spokes fading in turn. */
function Spinner({ size = 28 }: { size?: number }) {
  return (
    <span className={styles.spinner} style={{ width: size, height: size }} role="presentation">
      {Array.from({ length: 12 }, (_, i) => <i key={i} style={{ transform: `rotate(${i * 30}deg)`, animationDelay: `${(i - 12) / 12}s` }} />)}
    </span>
  );
}

/** performance.now(), refreshed twice a second while `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(performance.now()), 500);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/** When the lookup started, the latest estimate of what is left (made when a page arrived) and how long it took in all. */
interface Timing {
  started: number;
  estimate?: { at: number; left: number };
  took?: number;
}

export function InsiderTrades() {
  const [{ ticker, range }, setState] = useState(initialState);
  const window = windowOf(range);

  const update = (next: { ticker: string | null; range: Range }) => {
    setState(next);
    writeUrl(next.ticker, next.range);
  };

  return (
    <main className={styles.page}>
      <Search current={ticker} onOpen={(next) => update({ ticker: next, range })}>
        <RangePicker key={JSON.stringify(range)} range={range} onChange={(next) => update({ ticker, range: next })} />
      </Search>
      {ticker ? <Ticker key={`${ticker}|${window.since}|${window.until}`} ticker={ticker} window={window} onRange={(next) => update({ ticker, range: next })} /> : (
        <div className={styles.empty}>
          <h2>Who is buying and selling?</h2>
          <p>Look up a stock to see every trade its officers, directors and large holders reported to the SEC, as far back as 2004, pinned on the price chart.</p>
        </div>
      )}
      <p className={styles.footer}>
        Source: SEC EDGAR Form 4 filings, fetched when you look a stock up and cleaned with <a href="https://github.com/wavebyrd/form4clean" target="_blank" rel="noreferrer">form4clean ↗</a>.
        End-of-day prices and market caps from Nasdaq; chart prices are adjusted for later stock splits, trade prices are as filed.
        Purchases and sales may be open-market or private. Not investment advice.
      </p>
    </main>
  );
}

/** The ticker box: suggestions by symbol or company name as you type, arrow keys to choose. */
function Search({ current, onOpen, children }: { current: string | null; onOpen: (ticker: string) => void; children: ReactNode }) {
  const [text, setText] = useState(current ?? "");
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const listId = useId();

  useEffect(() => {
    const query = text.trim();
    if (!open || !query || normalizeTicker(query) === current) {
      setOptions([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      suggest(query, controller.signal).then((found) => {
        setOptions(found);
        setActive(-1);
      }, () => {});
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text, open, current]);

  const choose = (ticker: string) => {
    setText(ticker);
    setOpen(false);
    setInvalid(false);
    onOpen(ticker);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const picked = options[active]?.ticker ?? normalizeTicker(text);
    if (picked) choose(picked);
    else setInvalid(true);
  };
  const onKey = (event: KeyboardEvent) => {
    if (!options.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + options.length) % options.length);
    } else if (event.key === "Escape") setOpen(false);
  };

  return (
    <div className={styles.searchCard}>
      <form className={styles.search} onSubmit={submit} role="search">
        <div className={styles.field}>
          <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5" /><path d="m13 13 4 4" /></svg>
          <input
            type="text" value={text} placeholder="Search a ticker or company, e.g. Apple or NVDA" aria-label="Ticker or company"
            role="combobox" aria-expanded={open && options.length > 0} aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined} aria-invalid={invalid}
            autoComplete="off" spellCheck={false}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
              setInvalid(false);
            }}
            onKeyDown={onKey} onBlur={() => setTimeout(() => setOpen(false), 150)} onFocus={() => setOpen(true)}
          />
          {open && options.length > 0 && (
            <ul className={styles.options} id={listId} role="listbox">
              {options.map((o, i) => (
                <li key={o.ticker} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(o.ticker)}>
                  <strong>{o.ticker}</strong><span>{o.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="submit">Look up</button>
      </form>
      <div className={styles.popular}>
        <span>Popular</span>
        {EXAMPLES.map((t) => <button key={t} type="button" onClick={() => choose(t)} aria-pressed={t === current}>{t}</button>)}
      </div>
      {invalid && <p className={styles.invalid}>Type a ticker like AAPL or BRK.B, or pick a company from the list.</p>}
      {children}
    </div>
  );
}

/** How far back to look: the presets, or From and To dates applied together. */
function RangePicker({ range, onChange }: { range: Range; onChange: (next: Range) => void }) {
  const custom = !("preset" in range);
  const [editing, setEditing] = useState(custom);
  const [since, setSince] = useState(custom ? range.since : monthsBefore(today(), 24));
  const [until, setUntil] = useState(custom ? range.until : today());
  const valid = customWindow(since, until, today());
  return (
    <div className={styles.range}>
      <div className={styles.segments} role="group" aria-label="Look back">
        <span>Look back</span>
        {LOOKBACKS.map(([id, label]) => (
          <button key={id} type="button" aria-pressed={!editing && "preset" in range && range.preset === id} onClick={() => {
            setEditing(false);
            onChange({ preset: id });
          }}>{label}</button>
        ))}
        <button type="button" aria-pressed={editing} onClick={() => setEditing(true)}>Custom</button>
      </div>
      {editing && (
        <form className={styles.custom} onSubmit={(event) => {
          event.preventDefault();
          if (valid) onChange(valid);
        }}>
          <label>From <input type="date" min={EARLIEST} max={today()} value={since} onChange={(e) => setSince(e.target.value)} /></label>
          <label>To <input type="date" min={EARLIEST} max={today()} value={until} onChange={(e) => setUntil(e.target.value)} /></label>
          <button type="submit" disabled={!valid}>Apply</button>
          {!valid && <span className={styles.invalidInline}>Pick a start before the end, between 2004 and today.</span>}
        </form>
      )}
    </div>
  );
}

function Ticker({ ticker, window, onRange }: { ticker: string; window: Window; onRange: (next: Range) => void }) {
  const [page, setPage] = useState<TickerPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<ReadonlySet<TradeType>>(new Set<TradeType>(["purchase", "sale"]));
  const [minValue, setMinValue] = useState(0);
  const span = monthsBetween(window.since, window.until);
  const [months, setMonths] = useState(span > 12 ? 12 : 0);
  const [rows, setRows] = useState(ROWS);
  const [hovered, setHovered] = useState<PinGroup | null>(null);
  const firstPage = useRef<Pick<TickerPage, "prices" | "cap">>({});
  const [timing, setTiming] = useState<Timing>(() => ({ started: performance.now() }));
  // A range with more than LONG_FILINGS filings stops after the first page until the visitor says go on.
  const [paused, setPaused] = useState(false);
  const [goOn, setGoOn] = useState(false);
  const [throttled, setThrottled] = useState(false);

  // Newest page first, then one more page at a time until the whole range is in.
  useEffect(() => {
    const controller = new AbortController();
    const started = performance.now();
    const samples: Sample[] = [];
    setTiming({ started });
    setPaused(false);
    (async () => {
      let pages = 1;
      let step = 1;
      while (!controller.signal.aborted) {
        const asked = performance.now();
        const result = await lookUp(ticker, pages, window, controller.signal);
        if (!result.ok && result.limited) {
          // The API allows each visitor a set number of requests a minute; wait, then ask again.
          setThrottled(true);
          await sleep(10_000, controller.signal);
          setThrottled(false);
          continue;
        }
        if (!result.ok) {
          setError(pages === 1 ? result.error : `Stopped loading older filings: ${result.error}`);
          return;
        }
        if (pages === 1) firstPage.current = { prices: result.page.prices, cap: result.page.cap };
        setPage({ ...result.page, ...firstPage.current });
        const now = performance.now();
        const elapsed = (now - started) / 1000;
        // A page from the lookup service's memory says nothing about how fast the SEC is.
        if (now - asked < 1000) samples.length = 0;
        samples.push({ at: elapsed, loaded: result.page.filings });
        setTiming({
          started,
          estimate: { at: now, left: secondsLeft(samples, result.page.totalFilings) },
          took: result.page.complete ? elapsed : undefined,
        });
        if (result.page.complete) return;
        if (pages === 1 && !goOn && result.page.totalFilings > LONG_FILINGS) {
          setPaused(true);
          return;
        }
        ({ pages, step } = nextPages(pages, step, performance.now() - asked));
      }
    })().catch((e: Error) => {
      if (!controller.signal.aborted) setError(`The lookup failed (${e.message}). Try again in a minute.`);
    });
    return () => controller.abort();
  }, [ticker, window.since, window.until, goOn]);

  const all = useMemo(() => page?.trades ?? [], [page]);
  const bars = useMemo(() => page?.prices ?? [], [page]);
  const trades = useMemo(() => filterTrades(all, types, minValue), [all, types, minValue]);
  const summary = useMemo(() => summarize(all), [all]);
  const present = useMemo(() => new Set(all.map((t) => t.type)), [all]);
  const now = useNow(!error && timing.took === undefined);
  const elapsed = (now - timing.started) / 1000;

  if (!page) {
    return error ? <p className={styles.error}>{error}</p> : (
      <div className={styles.loading} role="status">
        <Spinner size={34} />
        <p className={styles.loadingTitle}>Fetching {ticker}'s filings from the SEC</p>
        <p className={styles.loadingClock}>{clock(elapsed)}</p>
        <p className={styles.loadingHint}>
          The newest trades show in a few seconds. The whole range takes from a few seconds to a few minutes, depending
          on how many filings the company made in it; the SEC sends them one at a time.
        </p>
      </div>
    );
  }

  const last = bars.at(-1);
  const net = summary.buyValue - summary.sellValue;
  const list = hovered ? hovered.trades : trades.slice(0, rows);
  return (
    <section className={styles.panel} aria-labelledby="ticker-title">
      <header className={styles.panelHead}>
        <div>
          <h2 id="ticker-title">{page.ticker}{clusterBuying(all) && <span className={styles.badge} title="Three or more insiders bought within 14 days">Cluster buying</span>}</h2>
          <p className={styles.issuer}>{page.issuer}</p>
        </div>
        <div className={styles.quote}>
          {last && <strong>{price(last[1])}</strong>}
          <span>{last ? `Close ${last[0]}` : ""}{page.cap != null ? ` · ${capLabel(page.cap)} · ${capSize(page.cap)}` : ""}</span>
        </div>
      </header>

      {paused
        ? <LongLoad page={page} window={window} onLoadAll={() => setGoOn(true)} onRange={onRange} />
        : <Progress page={page} window={window} trades={all.length} timing={timing} elapsed={elapsed} error={error} throttled={throttled} />}

      <div className={styles.tiles}>
        <div><span>Buys</span><strong className={styles.up}>{count(summary.buys)}</strong><small>{compactUsd(summary.buyValue)}</small></div>
        <div><span>Sells</span><strong className={styles.down}>{count(summary.sells)}</strong><small>{compactUsd(summary.sellValue)}</small></div>
        <div><span>Net</span><strong className={net > 0 ? styles.up : net < 0 ? styles.down : undefined}>{net > 0 ? "+" : ""}{compactUsd(net)}</strong><small>buys minus sells</small></div>
        <div><span>Insiders</span><strong>{count(summary.insiders)}</strong><small>with a trade on file</small></div>
      </div>

      <div className={styles.controls}>
        <fieldset className={styles.chips}>
          <legend>Type</legend>
          {TRADE_TYPES.filter((t) => present.has(t) || types.has(t)).map((t) => (
            <button key={t} type="button" aria-pressed={types.has(t)} data-type={t} onClick={() => setTypes(toggle(types, t))}>{TYPE_LABELS[t]}</button>
          ))}
        </fieldset>
        <div className={styles.segments} role="group" aria-label="Trade size">
          <span>Size</span>
          {SIZES.map(([value, label]) => <button key={value} type="button" aria-pressed={minValue === value} onClick={() => setMinValue(value)}>{label}</button>)}
        </div>
      </div>

      {bars.length ? (
        <div className={styles.chartBox}>
          <div className={styles.segments} role="group" aria-label="Chart range">
            {CHART_RANGES.filter(([value]) => value === 0 || value < span).map(([value, label]) => (
              <button key={label} type="button" aria-pressed={months === value} onClick={() => setMonths(value)}>{label}</button>
            ))}
          </div>
          {bars[0]![0] > monthsBefore(window.since, -1) && <p className={styles.priceNote}>Price history starts {fromDay(bars[0]![0])}; Nasdaq keeps ten years.</p>}
          <InsiderChart bars={bars} trades={trades} months={months} onHover={setHovered} />
        </div>
      ) : <p className={styles.notice}>No price history for {page.ticker}. It may be unlisted, an OTC stock or a fund.</p>}

      <h3 className={styles.listTitle}>{hovered ? `${hovered.label} · ${count(hovered.trades.length)} trades` : `${count(trades.length)} trades`}</h3>
      {list.length ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Date</th><th>Insider</th><th>Type</th><th className={styles.num}>Shares</th><th className={styles.num}>Price</th><th className={styles.num}>Value</th><th>Filing</th></tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id}>
                  <td className={styles.time}>{t.date ?? "—"}</td>
                  <td>{t.insider ?? "—"}<span className={styles.sub}>{t.title ?? t.role}</span></td>
                  <td><span className={styles.type} data-type={t.type}>{TYPE_LABELS[t.type]}</span>{t.plan && <span className={styles.sub}>10b5-1 plan</span>}</td>
                  <td className={styles.num}>{shares(t.shares)}</td>
                  <td className={styles.num}>{t.price == null ? "—" : price(t.price)}</td>
                  <td className={styles.num}>{compactUsd(t.value)}</td>
                  <td>{t.url ? <a href={t.url} target="_blank" rel="noreferrer">Form {t.form} ↗</a> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className={styles.notice}>{all.length ? "No trades match these filters." : `No insider trades filed for ${page.ticker} ${describe(window)}.`}</p>}
      {!hovered && trades.length > rows && (
        <button type="button" className={styles.more} onClick={() => setRows(rows + ROWS)}>Show {count(Math.min(ROWS, trades.length - rows))} more</button>
      )}
    </section>
  );
}

/** Shown instead of the loading card when the range holds more filings than load in a couple of minutes. */
function LongLoad({ page, window, onLoadAll, onRange }: { page: TickerPage; window: Window; onLoadAll: () => void; onRange: (next: Range) => void }) {
  const shorter = shorterPreset(page.totalFilings, monthsBetween(window.since, window.until));
  return (
    <div className={styles.longLoad} role="alert">
      <strong>{page.ticker} filed {count(page.totalFilings)} insider reports {describe(window)}.</strong>
      <p>
        The SEC sends them one at a time, so loading them all takes about {clock(secondsLeft([], page.totalFilings))}, longer
        while others are looking stocks up. The newest {count(page.filings)} are below.
      </p>
      <div className={styles.longLoadActions}>
        <button type="button" className={styles.primary} onClick={onLoadAll}>Load all {count(page.totalFilings)}</button>
        <button type="button" onClick={() => onRange({ preset: shorter.id })}>
          {shorter.label} instead · about {clock(secondsLeft([], shorter.filings))}
        </button>
      </div>
    </div>
  );
}

/** The loading card: time so far and left, how far back the filings reach, with the years marked. */
function Progress({ page, window, trades, timing, elapsed, error, throttled }: {
  page: TickerPage;
  window: Window;
  trades: number;
  timing: Timing;
  elapsed: number;
  error: string | null;
  throttled: boolean;
}) {
  const percent = Math.round(reachedPercent(window.since, page.through, window.until, page.complete));
  const years = yearSegments(window.since, window.until);
  // Label every year while there is room; on long ranges, every second or fifth.
  const step = [1, 2, 5].find((n) => (100 / years.length) * n >= 6) ?? 10;
  const left = timing.estimate ? Math.max(0, timing.estimate.left - (performance.now() - timing.estimate.at) / 1000) : null;
  const done = page.complete;
  return (
    <div className={styles.progress} data-done={done} role="status">
      <div className={styles.progressHead}>
        {done ? <span className={styles.check} aria-hidden>✓</span> : <Spinner size={18} />}
        <strong>{done ? `Loaded in ${clock(timing.took ?? elapsed)}` : `Loading insider filings ${describe(window)}`}</strong>
        <span className={styles.timer}>
          {done ? `${count(page.totalFilings)} filings · ${count(trades)} trades`
            : <><b>{clock(elapsed)}</b> elapsed · {left === null ? "estimating…" : left < 3 ? "almost done" : <>about <b>{clock(left)}</b> left</>}</>}
        </span>
      </div>
      <div className={styles.track} role="progressbar" aria-label="Share of the range loaded" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <i style={{ width: `${percent}%` }} />
        {years.slice(0, -1).map((y) => <s key={y.year} style={{ left: `${y.to}%` }} />)}
      </div>
      <div className={styles.years} aria-hidden>
        {years.filter((y) => y.year % step === 0 && y.to - y.from >= 3).map((y) => (
          <span key={y.year} style={{ left: `${(y.from + y.to) / 2}%` }} data-reached={percent >= y.to || done}>{y.year}</span>
        ))}
      </div>
      <div className={styles.progressStats}>
        <span><b>{count(trades)}</b> trades</span>
        <span><b>{count(page.filings)}</b> of {count(page.totalFilings)} filings</span>
        <span>back to <b>{fromDay(page.through)}</b></span>
        <span className={styles.percent}>{percent}%</span>
      </div>
      {throttled && <p className={styles.throttled}>Pausing a few seconds: the site limits how many requests one visitor can make a minute.</p>}
      {error && <p className={styles.errorInline}>{error}</p>}
    </div>
  );
}
